import React from 'react'
import IntegrationCard from '@/features/integrations/components/IntegrationCard'
import { useHubSpotIntegration } from '../hooks/useHubSpotIntegration'
import type { IntegrationSummary } from '@/features/integrations/types'
import { useTheme } from '@/context/ThemeContext'
import { Info } from 'lucide-react'

const HubSpotLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 512 512" fill="none" aria-hidden="true">
    <path
      d="M395.3 199.8v-52.6a44.4 44.4 0 0 0 25.6-40.1V105c0-24.5-19.9-44.4-44.4-44.4h-2.1C349.9 60.6 330 80.5 330 105v2.1c0 17.2 9.8 32.1 24.2 39.5v53.5c-22.8 3.5-43.5 13.1-60.3 27.3L133.2 113.5a48.9 48.9 0 0 0 1.7-12.8C134.9 54.9 97 17 50.2 17S-34.5 54.9-34.5 101.7s37.9 84.7 84.7 84.7c17.6 0 34-5.4 47.5-14.6l159 113.5a132.4 132.4 0 0 0-5.9 39.2c0 27.8 8.6 53.6 23.3 74.9L230.5 443c-3.1-.7-6.3-1.1-9.6-1.1-24.7 0-44.7 20-44.7 44.7s20 44.7 44.7 44.7 44.7-20 44.7-44.7c0-7.4-1.8-14.4-5-20.5l42.1-43.1a132.9 132.9 0 0 0 74.6 22.9c73.5 0 133.1-59.6 133.1-133.1.1-62.9-43.7-115.7-115.1-112.9zm-19-141.2h2.1a46.4 46.4 0 1 1 0 92.8h-2.1a46.4 46.4 0 1 1 0-92.8zM50.2 169.4C12.7 169.4-17.5 139.2-17.5 101.7S12.7 34 50.2 34s67.7 30.2 67.7 67.7-30.3 67.7-67.7 67.7zm327.1 268.2c-63.9 0-115.7-51.8-115.7-115.7S313.4 206.2 377.3 206.2 493 258 493 321.9s-51.8 115.7-115.7 115.7z"
      fill="#FF7A59"
    />
  </svg>
)

type Props = {
  workspaceId: string
  summary: IntegrationSummary | null | undefined
  onRefresh: () => void
}

const HubSpotIntegrationCard: React.FC<Props> = ({ workspaceId, summary, onRefresh }) => {
  const { currentTheme: t } = useTheme()

  const { actionStatus, connect, disconnect } = useHubSpotIntegration(
    workspaceId,
    summary ?? null,
    onRefresh,
  )

  const detail = summary?.identifier ?? summary?.displayName
  const status = summary?.status ?? 'not_configured'

  // Gating connection: members cannot connect if the portal is not bound yet
  const isDisconnected = status !== 'active' && status !== 'invalid'
  const isPortalBound = !!summary?.workspaceHubspotPortalId
  const isMember = summary?.userRole === 'member'
  const hideConnectButton = isDisconnected && !isPortalBound && isMember

  return (
    <>
      <IntegrationCard
        icon={<HubSpotLogo />}
        name="HubSpot"
        description="Sync contacts, deals, and outreach with your CRM."
        status={status}
        {...(detail ? { connectedDetail: detail } : {})}
        connecting={actionStatus === 'connecting'}
        disconnecting={actionStatus === 'disconnecting'}
        flash={actionStatus === 'error' ? 'error' : null}
        onConnect={hideConnectButton ? undefined : connect}
        onDisconnect={disconnect}
        onClearFlash={() => {}}
        connectLabel="Connect HubSpot"
        disconnectConfirmTitle="Disconnect HubSpot"
        disconnectConfirmMessage={`Remove the HubSpot integration for ${summary?.displayName ?? 'this workspace'}? Zeph will stop reading and updating your CRM immediately.`}
      >
        {hideConnectButton && (
          <div
            className="flex items-start gap-2.5 mt-3.5 pt-3.5"
            style={{ borderTop: `1px solid ${t.border}` }}
          >
            <Info size={15} className="shrink-0 mt-0.5 text-amber-500" />
            <span className="text-xs leading-normal" style={{ color: t.mutedText }}>
              Waiting for a workspace owner or admin to connect the initial HubSpot portal.
            </span>
          </div>
        )}

        {isDisconnected && isPortalBound && (
          <div
            className="flex items-start gap-2.5 mt-3.5 pt-3.5"
            style={{ borderTop: `1px solid ${t.border}` }}
          >
            <Info size={15} className="shrink-0 mt-0.5 text-blue-500" />
            <span className="text-xs leading-normal" style={{ color: t.mutedText }}>
              This workspace is bound to HubSpot Portal ID: <strong style={{ color: t.text }}>{summary?.workspaceHubspotPortalId}</strong>. You must connect an account belonging to this portal.
            </span>
          </div>
        )}
      </IntegrationCard>
    </>
  )
}

export default React.memo(HubSpotIntegrationCard)
